<template>
  <div class="flag-detail-container">
    <!-- Not Found -->
    <div v-if="!config" class="not-found-state">
      <i class="fas fa-flag"></i>
      <h3>Flag Not Found</h3>
      <p>The feature flag "<strong>{{ featureKey }}</strong>" does not exist.</p>
      <button class="btn-back" @click="goBack">
        <i class="fas fa-arrow-left me-2"></i> Back to Flags
      </button>
    </div>

    <template v-else>
      <!-- Page Header -->
      <o-bread-crumb url="/extranet/feature-flags">
        <template v-slot:pageName>Feature Flags</template>
        <template v-slot:pageDetail>{{ config?.name || featureKey }}</template>
        <template v-slot:pageDescription><br/></template>
      </o-bread-crumb>

      <!-- Loading -->
      <div v-if="page.isLoading" class="detail-loading">
        <div class="spinner-border text-danger" role="status"></div>
        <p>Loading flag details...</p>
      </div>

      <div v-else class="detail-body">
        <!-- ── Row 1: Status Bar ── -->
        <div class="status-bar-card">
          <div class="status-bar-left">
            <span class="status-dot" :class="flagData?.is_enabled ? 'dot-active' : 'dot-inactive'"></span>
            <span class="status-text" :class="flagData?.is_enabled ? 'text-active' : 'text-inactive'">
              {{ flagData?.is_enabled ? 'ACTIVE' : 'INACTIVE' }}
            </span>
            <span class="status-scope-badge">{{ scopeLabel }}</span>
          </div>
          <div class="status-bar-right">
            <p v-if="isPromotionsForceDisabled" class="feature-change-info me-3 mb-0">
              <i class="fas fa-info-circle me-1"></i>Automatically disabled because both&ensp;<strong>Point Control</strong>&ensp;and&ensp;<strong>Benefit Module</strong>&ensp;are NONACTIVE.
            </p>
            <p v-if="isVoucherGiftForceDisabled" class="feature-change-info me-3 mb-0">
              <i class="fas fa-info-circle me-1"></i>Automatically disabled because&ensp;<strong>Point Control</strong>&ensp;is NONACTIVE.
            </p>

            <feature-flag-toggle
              v-if="flagData"
              :key="`toggle-detail-${toggleResetCounter}`"
              :flag="flagData"
              :disabled="flagData.isInherited || isPromotionsForceDisabled || isVoucherGiftForceDisabled"
              :loading="toggleLoading"
              @toggle="handleToggleClick"
            />
          </div>
        </div>

        <!-- ── Row 2: Functional Overview ── -->
        <div class="detail-card overview-card">
          <div class="card-label">FUNCTIONAL OVERVIEW</div>
          <h2 class="card-main-title">{{ config.name }}</h2>
          <p class="card-overview-text">{{ config.overview }}</p>

          <!-- Highlight Cards -->
          <div class="highlight-grid">
            <div
              v-for="(item, idx) in config.highlights"
              :key="idx"
              class="highlight-item"
            >
              <i :class="item.icon" class="highlight-icon"></i>
              <div>
                <div class="highlight-title">{{ item.title }}</div>
                <div class="highlight-desc">{{ item.desc }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Row 3: Behavior Cards (Single Container) ── -->
        <div class="detail-card behavior-container">
          <!-- OFF Behavior -->
          <div class="behavior-column behavior-off">
            <div class="behavior-header">
              <i class="far fa-times-circle behavior-icon icon-off"></i>
              <span class="behavior-label label-off">System Baseline (OFF)</span>
            </div>
            <ul class="behavior-list">
              <li v-for="(item, idx) in config.offBehavior" :key="idx" class="item-off">
                <i class="fas fa-times list-icon-off"></i>
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>

          <!-- ON Behavior -->
          <div class="behavior-column behavior-on">
            <div class="behavior-header">
              <i class="fas fa-check-circle behavior-icon icon-on"></i>
              <span class="behavior-label label-on">Active Behavior (ON)</span>
            </div>
            <ul class="behavior-list">
              <li v-for="(item, idx) in config.onBehavior" :key="idx" class="item-on">
                <i :class="['fas', getOnIcon(idx), 'list-icon-on']"></i>
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- ── Row 4: Hierarchy + Audit Logs ── -->
        <div class="bottom-row">
          <!-- Hierarchy -->
          <div class="detail-card hierarchy-card">
            <div class="card-label">INHERITANCE LOGIC</div>
            <h3 class="card-sub-title">Hierarchy Enforcement</h3>
            <div class="hierarchy-tree">
              <div
                v-for="level in normalizedHierarchy"
                :key="level.iconKey"
                class="hierarchy-node"
                :class="{ 'node-master': level.isMaster }"
                :style="{ paddingLeft: level.index * 32 + 'px' }"
              >
                <div class="node-inner">
                  <div class="node-icon-wrap">
                    <i :class="levelIconMap[level.iconKey] || 'fas fa-layer-group'"></i>
                  </div>
                  <div class="node-text">
                    <span class="node-label">{{ level.label }}</span>
                    <span class="node-sublabel">{{ level.sublabel }}</span>
                  </div>
                  <span class="node-badge" :class="`badge-${level.iconKey}`">
                    <i v-if="level.badge === 'INHERITED'" class="fas fa-lock me-1"></i>
                    {{ level.badge }}
                  </span>
                </div>
                <!-- Connector line (not on last) -->
                <div v-if="level.index !== normalizedHierarchy.length - 1" class="node-connector"></div>
              </div>
            </div>
          </div>

          <!-- Recent Audit Logs Preview -->
          <div class="audit-preview-card">
            <!-- Header: label + View All -->
            <div class="audit-preview-header">
              <span class="audit-preview-label">AUDIT LOGS</span>
              <button class="btn-view-all-audit" @click="goToAudit">View All</button>
            </div>

            <!-- Empty -->
            <div v-if="auditPreview.length === 0" class="audit-empty">
              <i class="fas fa-clipboard-list"></i>
              <span>No audit records yet</span>
            </div>

            <!-- Log items -->
            <div v-else class="audit-list">
              <div v-for="(log, idx) in auditPreview" :key="idx" class="audit-item">
                <div class="audit-dot-wrap">
                  <span
                    class="audit-dot"
                    :class="log.new_flag === 'ACTIVE' || log.new_flag === 'DECENTRALIZE' ? 'dot-green' : 'dot-red'"
                  ></span>
                </div>
                <div class="audit-content">
                  <span class="audit-action">{{ formatAuditAction(log) }}</span>
                  <span class="audit-actor">
                    {{ log.changed_by_name || 'System (Auto-generated)' }} · {{ formatDate(log.changed_at) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Bottom border section (separator) -->
            <div class="audit-bottom-section">
              <button class="btn-goto-audit-full" @click="goToAudit">
                <i class="fas fa-history me-2"></i>View full audit history
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Confirm Toggle Modal -->
    <o-modal v-if="showConfirmModal" @close="discardToggle">
      <template v-slot:header>
        <h5 class="modal-title fw-bold">Confirm Flag Update</h5>
      </template>
      <template v-slot:body>
        <div class="confirm-modal-content">
          <div class="confirm-icon" :class="pendingNewValue ? 'enable' : 'disable'">
            <i class="fas" :class="pendingNewValue ? 'fa-check' : 'fa-times'"></i>
          </div>
          <p class="confirm-text">
            Are you sure you want to <strong>{{ pendingNewValue ? 'ENABLE' : 'DISABLE' }}</strong> this feature?
          </p>
          <div class="flag-info-card">
            <div class="flag-info-key">{{ config?.name }}</div>
            <div class="flag-info-desc">{{ config?.description }}</div>
          </div>
          <p class="text-muted small mt-3">This change will take effect immediately for all users in the selected tenant cluster.</p>
        </div>
      </template>
      <template v-slot:footer>
        <button type="button" class="btn btn-light-ext" @click="discardToggle">Discard</button>
        <button
          type="button"
          class="btn"
          :class="pendingNewValue ? 'btn-success-ext' : 'btn-danger-ext'"
          @click="confirmToggle"
        >
          Yes, Update Flag
        </button>
      </template>
    </o-modal>

    <o-modal v-if="showPointControlWarningModal" @close="cancelPointControlToggle" modal-class="warning-modal-wide">
      <template v-slot:header>
        <h5 class="modal-title fw-bold d-flex align-items-center gap-2">
          <i class="fas fa-exclamation-triangle" style="color: #f59e0b; font-size: 20px;"></i>
          System Impact Warning
        </h5>
      </template>
      <template v-slot:body>
        <div class="warning-modal-content">
          <div class="warning-icon-wrap">
            <i class="fas fa-power-off"></i>
          </div>
          <p class="warning-modal-title">Disabling {{ featureKey === 'benefit_module' ? 'Benefit Module' : 'Point Control' }} has cascading effects</p>
          <p class="warning-modal-desc">
            You are about to disable
            <strong>{{ featureKey === 'benefit_module' ? 'Benefit Module' : 'Point Control Mode' }}</strong>.
            Because <strong>Promotions Module</strong> is currently active and depends on the point system,
            it will be <strong>automatically disabled</strong> as well.
          </p>
          <div class="warning-impact-card">
            <div class="impact-row">
              <span class="impact-badge badge-off"><i class="fas fa-times me-1"></i>Will be Disabled</span>
              <div class="impact-flags">
                <span class="impact-flag-item">
                  <i :class="featureKey === 'benefit_module' ? 'fas fa-crown' : 'fas fa-align-justify'" class="me-1"></i>
                  {{ featureKey === 'benefit_module' ? 'Benefit Module' : 'Point Control Mode' }}
                </span>
                <span class="impact-flag-item"><i class="fas fa-tags me-1"></i>Promotions Module</span>
                <span v-if="featureKey === 'point_control'" class="impact-flag-item"><i class="fas fa-ticket-alt me-1"></i>Voucher Gift</span>
              </div>
            </div>
          </div>
          <p class="warning-modal-note">
            This action cannot be undone automatically. You will need to manually re-enable each module.
          </p>
        </div>
      </template>
      <template v-slot:footer>
        <button type="button" class="btn btn-warning-cancel" @click="cancelPointControlToggle">Cancel</button>
        <button type="button" class="btn btn-warning-confirm" @click="proceedPointControlToggle">
          <i class="fas fa-power-off me-2"></i>Yes, Disable Both
        </button>
      </template>
    </o-modal>

    <o-modal v-if="showProductWarningModal" @close="cancelProductToggle" modal-class="warning-modal-wide">
      <template v-slot:header>
        <h5 class="modal-title fw-bold d-flex align-items-center gap-2">
          <i class="fas fa-exclamation-triangle" style="color: #f59e0b; font-size: 20px;"></i>
          System Impact Warning
        </h5>
      </template>
      <template v-slot:body>
        <div class="warning-modal-content">
          <div class="warning-icon-wrap">
            <i class="fas fa-power-off"></i>
          </div>
          <p class="warning-modal-title">Disabling {{ featureKey === 'merchant_product' ? 'Merchant Product' : 'Branch Product Management' }} has cascading effects</p>
          <p class="warning-modal-desc">
            You are about to disable
            <strong>{{ featureKey === 'merchant_product' ? 'Merchant Product' : 'Branch Product Management' }}</strong>.
            Because <strong>{{ featureKey === 'merchant_product' ? 'Branch Product Management' : 'Merchant Product' }}</strong> is currently NONACTIVE,
            this will impact <strong>POS Membership</strong>, which will no longer be usable for creating transactions.
          </p>
          <div class="warning-impact-card">
            <div class="impact-row">
              <span class="impact-badge badge-off"><i class="fas fa-times me-1"></i>Impacted System</span>
              <div class="impact-flags">
                <span class="impact-flag-item">
                  <i class="fas fa-calculator me-1"></i>
                  POS Membership (Create Transaction)
                </span>
              </div>
            </div>
          </div>
          <p class="warning-modal-note">
            Are you sure you want to proceed?
          </p>
        </div>
      </template>
      <template v-slot:footer>
        <button type="button" class="btn btn-warning-cancel" @click="cancelProductToggle">Cancel</button>
        <button type="button" class="btn btn-warning-confirm" @click="proceedProductToggle">
          <i class="fas fa-power-off me-2"></i>Yes, Disable
        </button>
      </template>
    </o-modal>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import api from '@/helpers/api'
import { OModal, OBreadCrumb } from '@/components/OmnihControl'
import FeatureFlagToggle from '@/components/extranet/FeatureFlagToggle.vue'
import { useFeatureFlagConfig } from '@/composables/useFeatureFlagConfig'
import { useToast } from '@/composables/useToast'

export default {
  name: 'ExtranetFeatureFlagDetail',

  components: { OModal, OBreadCrumb, FeatureFlagToggle },

  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const { showToast } = useToast()
    const { getConfig, LEVEL_ICON_MAP } = useFeatureFlagConfig()

    const featureKey = computed(() => route.params.featureKey)
    const config = computed(() => getConfig(featureKey.value))

    const allFlagsData = ref(null)
    const page = reactive({ isLoading: false })
    const flagData = ref(null)
    const auditPreview = ref([])
    const toggleLoading = ref(false)
    const showConfirmModal = ref(false)
    const pendingNewValue = ref(false)
    const toggleResetCounter = ref(0)
    const isPromotionsForceDisabled = ref(false)
    const isVoucherGiftForceDisabled = ref(false)
    const showPointControlWarningModal = ref(false)
    const showProductWarningModal = ref(false)

    const activeCorporateId = computed(() => store.state.extranet?.selectedTenant?.id)

    const scopeLabel = computed(() => {
      const map = {
        system: 'System Flag',
        corporate: 'Corporate Level Master',
        branch: 'Branch Level',
        merchant: 'Merchant Level'
      }
      return map[config.value?.scope] || ''
    })

    const normalizedHierarchy = computed(() => {
      const hierarchy = config.value?.hierarchy;
      if (!hierarchy) return [];
      if (Array.isArray(hierarchy)) {
        return hierarchy.map((item, idx) => ({
           ...item,
           iconKey: item.key,
           index: idx
        }));
      }
      return Object.entries(hierarchy).map(([key, item], idx) => ({
        ...item,
        iconKey: key,
        index: idx
      }));
    });

    const levelIconMap = LEVEL_ICON_MAP

    async function loadFlagDetail() {
      if (!activeCorporateId.value || !featureKey.value) return

      page.isLoading = true
      try {
        const response = await api.request('get', `/api/extranet/feature-flags/${activeCorporateId.value}`)
        if (response.data?.data) {
          const allFlags = response.data.data
          allFlagsData.value = allFlags
          const raw = allFlags[featureKey.value]

          if (featureKey.value === 'promotions_module') {
            const pointFlag = allFlags['point_control']
            const benefitFlag = allFlags['benefit_module']
            
            const isPointNonActive = !pointFlag || pointFlag.flag === 'NONACTIVE'
            const isBenefitNonActive = !benefitFlag || benefitFlag.flag === 'NONACTIVE'
            
            isPromotionsForceDisabled.value = isPointNonActive && isBenefitNonActive
            isVoucherGiftForceDisabled.value = false
            
            if (isPromotionsForceDisabled.value && raw) {
              raw.flag = 'NONACTIVE'
            }
          } else if (featureKey.value === 'voucher_gift') {
            const pointFlag = allFlags['point_control']
            const isPointNonActive = !pointFlag || pointFlag.flag === 'NONACTIVE'

            isVoucherGiftForceDisabled.value = isPointNonActive
            isPromotionsForceDisabled.value = false

            if (isVoucherGiftForceDisabled.value && raw) {
              raw.flag = 'NONACTIVE'
            }
          } else {
            isPromotionsForceDisabled.value = false
            isVoucherGiftForceDisabled.value = false
          }

          if (raw && raw.feature) {
            const isPointControl = raw.feature === 'point_control'
            const isEnabled = isPointControl
              ? (raw.flag === 'CENTRALIZE' || raw.flag === 'DECENTRALIZE')
              : raw.flag === 'ACTIVE'

            flagData.value = {
              ...raw,
              name: config.value?.name || raw.feature,
              description: config.value?.description || '',
              is_enabled: isEnabled,
              isInherited: false
            }
          }
        }

        // Load recent audit logs (last 3)
        await loadAuditPreview()
      } catch (err) {
        console.error('Error loading flag detail:', err)
      } finally {
        page.isLoading = false
      }
    }

    async function loadAuditPreview() {
      try {
        const response = await api.request(
          'get',
          `/api/extranet/feature-flags/${activeCorporateId.value}/audit-logs`,
          {
            meta: { page: 1, limit: 4 },
            filter: { feature: featureKey.value }
          }
        )
        const resData = response.data?.data
        auditPreview.value = resData?.data_res?.slice(0, 4) || []
      } catch (err) {
        console.error('Error loading audit preview:', err)
        auditPreview.value = []
      }
    }

    function handleToggleClick(flagId, newValue) {
      pendingNewValue.value = newValue

      if (!newValue && allFlagsData.value) {
        const isPointControl = featureKey.value === 'point_control'
        const isBenefitModule = featureKey.value === 'benefit_module'

        if (isPointControl || isBenefitModule) {
          const pointFlag = allFlagsData.value['point_control']
          const benefitFlag = allFlagsData.value['benefit_module']
          const promotionsFlag = allFlagsData.value['promotions_module']

          const isPointActiveNow = pointFlag && pointFlag.flag !== 'NONACTIVE'
          const isBenefitActiveNow = benefitFlag && benefitFlag.flag === 'ACTIVE'
          const isPromotionsActiveNow = promotionsFlag && promotionsFlag.flag === 'ACTIVE'
          
          const voucherGiftFlag = allFlagsData.value['voucher_gift']
          const isVoucherGiftActiveNow = voucherGiftFlag && voucherGiftFlag.flag === 'ACTIVE'

          const willPointBeActive = isPointControl ? false : isPointActiveNow
          const willBenefitBeActive = isBenefitModule ? false : isBenefitActiveNow

          // Trigger warning if promotions is active OR if point_control is being disabled while voucher_gift is active
          if ((!willPointBeActive && !willBenefitBeActive && isPromotionsActiveNow) || 
              (!willPointBeActive && isVoucherGiftActiveNow)) {
            showPointControlWarningModal.value = true
            return
          }
        }

        const isBranchProduct = featureKey.value === 'branch_product_management'
        const isMerchantProduct = featureKey.value === 'merchant_product'

        if (isBranchProduct || isMerchantProduct) {
          const branchProductFlag = allFlagsData.value['branch_product_management']
          const merchantProductFlag = allFlagsData.value['merchant_product']

          const isBranchProductActiveNow = branchProductFlag && branchProductFlag.flag === 'ACTIVE'
          const isMerchantProductActiveNow = merchantProductFlag && merchantProductFlag.flag === 'ACTIVE'

          const willBranchProductBeActive = isBranchProduct ? false : isBranchProductActiveNow
          const willMerchantProductBeActive = isMerchantProduct ? false : isMerchantProductActiveNow

          if (!willBranchProductBeActive && !willMerchantProductBeActive) {
            showProductWarningModal.value = true
            return
          }
        }
      }

      showConfirmModal.value = true
    }

    function cancelPointControlToggle() {
      toggleResetCounter.value++
      showPointControlWarningModal.value = false
    }

    async function proceedPointControlToggle() {
      showPointControlWarningModal.value = false
      toggleLoading.value = true

      try {
        const targetFlag = 'NONACTIVE'

        await api.request('put', `/api/extranet/feature-flags/${activeCorporateId.value}`, {}, {
          feature: featureKey.value,
          flag: targetFlag
        })

        await api.request('put', `/api/extranet/feature-flags/${activeCorporateId.value}`, {}, {
          feature: 'promotions_module',
          flag: 'NONACTIVE'
        })
        
        if (featureKey.value === 'point_control') {
          await api.request('put', `/api/extranet/feature-flags/${activeCorporateId.value}`, {}, {
            feature: 'voucher_gift',
            flag: 'NONACTIVE'
          })
        }

        showToast({
          type: 'success',
          title: 'Flags Updated',
          message: `Dependent modules have been disabled.`
        })

        await loadFlagDetail()
      } catch (error) {
        showToast({
          type: 'error',
          title: 'Update Failed',
          message: error.response?.data?.message || 'Failed to update feature flags'
        })
      } finally {
        toggleLoading.value = false
      }
    }

    function cancelProductToggle() {
      toggleResetCounter.value++
      showProductWarningModal.value = false
    }

    async function proceedProductToggle() {
      showProductWarningModal.value = false
      await confirmToggle()
    }

    function discardToggle() {
      toggleResetCounter.value++
      showConfirmModal.value = false
    }

    async function confirmToggle() {
      showConfirmModal.value = false
      toggleLoading.value = true

      try {
        const isPointControl = featureKey.value === 'point_control'
        const targetFlag = isPointControl
          ? (pendingNewValue.value ? 'DECENTRALIZE' : 'NONACTIVE')
          : (pendingNewValue.value ? 'ACTIVE' : 'NONACTIVE')

        await api.request('put', `/api/extranet/feature-flags/${activeCorporateId.value}`, {}, {
          feature: featureKey.value,
          flag: targetFlag
        })

        showToast({
          type: 'success',
          title: 'Flag Updated',
          message: `"${config.value?.name}" has been ${pendingNewValue.value ? 'enabled' : 'disabled'}.`
        })

        await loadFlagDetail()
      } catch (error) {
        showToast({
          type: 'error',
          title: 'Update Failed',
          message: error.response?.data?.message || 'Failed to toggle feature flag'
        })
      } finally {
        toggleLoading.value = false
      }
    }

    function goBack() {
      router.push({ name: 'ExtranetFeatureFlags' })
    }

    function goToAudit() {
      router.push({
        name: 'ExtranetFeatureFlagAuditLog',
        params: { corporateId: activeCorporateId.value },
        query: { feature: featureKey.value }
      })
    }

    function formatAuditAction(log) {
      const newFlag = log.new_flag || ''
      if (!log.old_flag || log.old_flag === 'NONE') {
        return `System Seeded (${newFlag})`
      }
      return `Set to ${newFlag}`
    }

    function formatDate(dateStr) {
      if (!dateStr) return ''
      try {
        return new Date(dateStr).toLocaleString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        })
      } catch {
        return dateStr
      }
    }

    onMounted(() => loadFlagDetail())

    watch(activeCorporateId, (val) => { if (val) loadFlagDetail() })
    watch(featureKey, () => loadFlagDetail())

    function getOnIcon(idx) {
      const icons = ['fa-bolt', 'fa-retweet', 'fa-chart-line', 'fa-check'];
      return icons[idx] || 'fa-bolt';
    }

    return {
      featureKey,
      config,
      getOnIcon,
      page,
      flagData,
      auditPreview,
      toggleLoading,
      showConfirmModal,
      showPointControlWarningModal,
      showProductWarningModal,
      pendingNewValue,
      scopeLabel,
      normalizedHierarchy,
      levelIconMap,
      toggleResetCounter,
      isPromotionsForceDisabled,
      isVoucherGiftForceDisabled,
      handleToggleClick,
      discardToggle,
      confirmToggle,
      cancelPointControlToggle,
      proceedPointControlToggle,
      cancelProductToggle,
      proceedProductToggle,
      goBack,
      goToAudit,
      formatDate,
      formatAuditAction
    }
  }
}
</script>

<style scoped>
.flag-detail-container {
  max-width: 1440px;
  margin: 0 auto;
}

/* ── Not Found ── */
.not-found-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 32px;
  text-align: center;
  color: #94a3b8;
}
.not-found-state i { font-size: 56px; margin-bottom: 20px; }
.not-found-state h3 { font-size: 22px; font-weight: 700; color: #374151; margin-bottom: 8px; }
.not-found-state p { font-size: 14px; color: #6b7280; margin-bottom: 24px; }



/* ── Loading ── */
.detail-loading {
  display: flex; flex-direction: column; align-items: center; gap: 16px;
  padding: 80px 0; text-align: center; color: #64748b;
}

/* ── Body ── */
.detail-body { display: flex; flex-direction: column; gap: 16px; }

/* ── Base Card ── */
.detail-card {
  background: white;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  padding: 28px;
}
.card-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 8px;
}
.card-main-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 12px;
}
.card-sub-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 20px;
}
.card-overview-text {
  font-size: 14px;
  color: #475569;
  line-height: 1.75;
  margin-bottom: 24px;
  white-space: pre-line;
}

/* ── Status Bar ── */
.status-bar-card {
  background: white;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  padding: 18px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.status-bar-left { display: flex; align-items: center; gap: 12px; }
.status-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-active { background: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.15); }
.dot-inactive { background: #cbd5e1; }
.status-text { font-size: 13px; font-weight: 700; }
.text-active { color: #059669; }
.text-inactive { color: #64748b; }
.status-scope-badge {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 3px 10px;
  border-radius: 20px;
}
.status-bar-right { display: flex; align-items: center; gap: 16px; }
.btn-audit-log {
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-audit-log:hover { border-color: var(--extranet-primary); color: var(--extranet-primary); }

.feature-change-info {
  font-size: 13px;
  color: #d97706;
  background-color: #fffbeb;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #fcd34d;
  margin: 0;
  display: flex;
  align-items: center;
}

/* ── Highlights Grid ── */
.highlight-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.highlight-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  align-items: flex-start;
}
.highlight-icon {
  font-size: 18px;
  color: var(--extranet-primary);
  flex-shrink: 0;
  margin-top: 2px;
}
.highlight-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}
.highlight-desc { font-size: 12px; color: #64748b; line-height: 1.5; }

/* ── Behavior Row (Single Container) ── */
.behavior-container {
  display: flex;
  padding: 0;
  overflow: hidden;
}
.behavior-column {
  flex: 1;
  padding: 24px 32px;
}
.behavior-off {
  border-right: 1px solid #e8edf3;
  background-color: #fcfcfc;
}
.behavior-on {
  background-color: #ffffff;
}

.behavior-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
}
.behavior-icon { font-size: 16px; }
.icon-off { color: #94a3b8; }
.icon-on { color: #dc2626; }
.behavior-label { font-size: 13px; font-weight: 700; }
.label-off { color: #64748b; }
.label-on { color: #dc2626; }

.behavior-list {
  list-style: none;
  padding: 0; margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.behavior-list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 13px;
  line-height: 1.5;
}
.item-off { color: #94a3b8; }
.item-on { color: #1e293b; }

.list-icon-off { color: #cbd5e1; font-size: 13px; margin-top: 3px; flex-shrink: 0; }
.list-icon-on { color: #dc2626; font-size: 13px; margin-top: 3px; flex-shrink: 0; }

/* ── Bottom Row ── */
.bottom-row {
  display: grid;
  grid-template-columns: 7fr 5fr;
  gap: 24px;
}

/* ── Hierarchy ── */
.hierarchy-tree {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.hierarchy-node { display: flex; flex-direction: column; }
.node-inner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid #e8edf3;
  background: #fdfdfd;
  transition: all 0.2s;
}
.node-master .node-inner {
  background: #0f172a;
  border-color: #0f172a;
}
.node-icon-wrap {
  width: 36px; height: 36px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.12);
  font-size: 14px;
}
.node-master .node-icon-wrap { color: white; }
.node-master .node-icon-wrap { background: rgba(255,255,255,0.15); }
.node-icon-wrap { background: #e8edf3; color: #475569; }
.node-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.node-label { font-size: 13px; font-weight: 700; }
.node-master .node-label { color: white; }
.node-label { color: #1e293b; }
.node-sublabel { font-size: 11px; }
.node-master .node-sublabel { color: rgba(255,255,255,0.6); }
.node-sublabel { color: #94a3b8; }
.node-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.3px;
}
.node-master .badge-corporate,
.node-master .badge-superadmin { background: rgba(255,255,255,0.2); color: white; }

.badge-corporate,
.badge-superadmin,
.badge-branch,
.badge-area,
.badge-branch_head,
.badge-merchant_owner,
.badge-merchant,
.badge-member { background: #e8edf3; color: #64748b; }

.badge-affected { background: #fef3c7; color: #92400e; }

/* system key fallback */
.badge-system { background: rgba(255,255,255,0.2); color: white; }
.node-connector {
  width: 2px;
  height: 24px;
  background: #cbd5e1;
  margin-left: 34px;
}

/* ── Audit Preview Card ── */
.audit-preview-card {
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.05);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.audit-preview-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}

.audit-preview-label {
  font-family: 'Manrope', sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #7C839B;
}

.btn-view-all-audit {
  font-family: 'Manrope', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  color: #B51A1F;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s;
}
.btn-view-all-audit:hover { opacity: 0.75; }

.audit-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 0;
  color: #94a3b8;
  font-size: 13px;
}
.audit-empty i { font-size: 28px; }

.audit-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.audit-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
}

.audit-dot-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 4px 0 0;
  flex-shrink: 0;
}

.audit-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  flex-shrink: 0;
}
.dot-green { background: #34C759; }
.dot-red   { background: #B51A1F; }

.audit-content {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.audit-action {
  font-family: 'Manrope', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  color: #1B1B1D;
  display: flex;
  align-items: center;
}

.audit-actor {
  font-family: 'Manrope', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 16px;
  color: #76777D;
  display: flex;
  align-items: center;
}

/* Bottom border section */
.audit-bottom-section {
  border-top: 1px solid #C6C6CD;
  padding-top: 20px;
  margin-top: 4px;
}

.btn-goto-audit-full {
  font-family: 'Manrope', sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #7C839B;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
}
.btn-goto-audit-full:hover { color: #B51A1F; }

/* ── Confirm Modal ── */
.confirm-modal-content { text-align: center; padding: 20px 0; }
.confirm-icon {
  width: 64px; height: 64px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px;
  font-size: 24px;
}
.confirm-icon.enable { background: #dcfce7; color: #166534; }
.confirm-icon.disable { background: #fee2e2; color: #991b1b; }
.flag-info-card { background: #f8fafc; border: 1px solid #e2e8f0; padding: 16px; border-radius: 12px; margin-top: 16px; }
.flag-info-key { font-weight: 800; color: #1e293b; font-size: 15px; }
.flag-info-desc { font-size: 13px; color: #64748b; margin-top: 4px; }
.btn-success-ext { background: #10b981; color: white; font-weight: 700; padding: 10px 24px; border-radius: 10px; border: none; }
.btn-danger-ext { background: #ef4444; color: white; font-weight: 700; padding: 10px 24px; border-radius: 10px; border: none; }
.btn-light-ext { background: #f1f5f9; color: #475569; font-weight: 600; padding: 10px 24px; border-radius: 10px; border: none; margin-right: 8px; }
.btn-back { padding: 10px 20px; background: var(--extranet-primary); color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; }

/* ── Warning Modal ── */
.warning-modal-content { text-align: center; padding: 8px 0; }
.warning-icon-wrap {
  width: 88px; height: 88px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px;
  font-size: 34px;
  background: #fef08a;
  color: #ca8a04;
  border: 12px solid #fef9c3;
}
.warning-modal-title { font-size: 18px; font-weight: 800; color: #1e293b; margin-bottom: 12px; }
.warning-modal-desc { font-size: 14px; color: #475569; margin-bottom: 20px; line-height: 1.65; }
.warning-modal-note {
  font-size: 13px; color: #6b7280;
  margin: 16px auto 0;
  max-width: 420px;
  line-height: 1.6;
}
.warning-impact-card {
  background: #fffbeb; border: 1.5px solid #fde68a;
  border-radius: 14px; padding: 20px 20px 20px 16px;
  text-align: left;
  margin-bottom: 0;
}
.impact-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  text-align: left;
}
.impact-badge {
  font-size: 12px; font-weight: 700; padding: 8px 14px; border-radius: 8px;
  white-space: nowrap; flex-shrink: 0; margin-top: 4px;
}
.badge-off { background: #fee2e2; color: #dc2626; }
.impact-flags { display: flex; flex-direction: column; gap: 10px;  }
.impact-flag-item {
  font-size: 13px; font-weight: 600; color: #1e293b;
  display: inline-flex; align-items: center; gap: 6px;
  background: white; border: 1px solid #e2e8f0;
  padding: 10px 16px; border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

/* Warning modal buttons */
.btn-warning-cancel {
  background: #f1f5f9;
  color: #374151;
  font-weight: 600;
  font-size: 15px;
  padding: 12px 32px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-warning-cancel:hover { background: #e2e8f0; }
.btn-warning-confirm {
  background: #ef4444;
  color: white;
  font-weight: 700;
  font-size: 15px;
  padding: 12px 32px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.2s;
}
.btn-warning-confirm:hover { background: #dc2626; }
</style>
